import { NamedObjectControl } from "./NamedObjectControl";
import { RemoveArsItemButton } from "./RemoveArsItemButton";
import { WhereClauseManager } from "./WhereClauseManager";

/**
 * Props for the GroupControl component.
 */
export interface GroupControlProps {
  /** RFC 6901 JSON Pointer path to the Group in ArsContext state */
  dataPath: string;
  /** Disabled state */
  disabled?: boolean;
  /** Sibling count */
  siblingCount: number;
}

/**
 * A component for managing Group entities with full CRUD capabilities.
 *
 * This component provides:
 * - NamedObjectControl for editing name, description, and label
 * - WhereClauseManager for managing where clause conditions
 * - RemoveButton for deleting the group
 *
 * @param props - Component props.
 * @param props.dataPath - RFC 6901 JSON Pointer path to the Group in ArsContext state.
 * @param props.disabled - Optional flag to disable editing controls.
 * @param props.siblingCount - Number of sibling items
 * @returns A React functional component rendering the group editor.
 */
export const GroupControl = ({
  dataPath,
  disabled = false,
  siblingCount,
}: GroupControlProps): React.ReactElement => {
  return (
    <div className="position-relative">
      <div
        className="position-absolute"
        style={{ zIndex: 1, top: "-0.75rem", right: "-.75rem" }}
      >
        <RemoveArsItemButton
          dataPath={dataPath}
          disabled={disabled}
          siblingCount={siblingCount}
        />
      </div>

      <NamedObjectControl
        dataPath={dataPath}
        canRename
        disabled={disabled}
      />

      <WhereClauseManager
        dataPath={dataPath}
        disabled={disabled}
      />
    </div>
  );
};

GroupControl.displayName = "GroupControl";
